import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { RadioGroupField as RHFRadioField } from "$components/rhf-fields";
import ValidationContext from "$routes/_lib/validation/validation-context";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import useExemptPayment from "../hooks/useExemptPayment";
import { EXEMPTION_TYPE } from "./formConstants";
import { useExemptionTypes } from "./territoryExemptions";
import { useValidationSchema } from "./validationSchema";

const useStyles = makeStyles((theme) => ({
  modal: {
    borderRadius: theme.spacing(3),
  },
  warningMessage: {
    color: theme.palette.grey[600],
    fontWeight: 700,
    marginLeft: theme.spacing(0.5),
  },
  warningIcon: {
    marginRight: theme.spacing(2),
  },
  errorContainer: {
    color: theme.palette.error.main,
    display: "flex",
    alignItems: "center",
  },
  errorMessage: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(0.5),
  },
}));

export const ExemptModal: FC<{
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  invoiceId: string;
  currency: string;
  totalPayableInPence: number;
}> = ({
  open,
  onClose,
  onCancel,
  invoiceId,
  currency,
  totalPayableInPence,
}) => {
  const classes = useStyles();
  const validationSchema = useValidationSchema();
  const exemptions = useExemptionTypes();
  const { exemptPayment } = useExemptPayment(invoiceId);
  const [busy, setBusy] = useState(false);

  const formMethods = useForm({
    mode: "onTouched",

    resolver: yupResolver(validationSchema),
  });

  const {
    watch,
    formState: { isDirty, isValid },
  } = formMethods;
  const exemptionName = watch(EXEMPTION_TYPE);

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await exemptPayment({
        reason: exemptionName,
        currency,
        totalPayableInPence,
      });
    } finally {
      onClose();
    }
  };

  return (
    <ValidationContext.Provider value={{ validationSchema }}>
      <FormProvider {...formMethods}>
        <form name="InvoiceExemptForm" data-testid="invoicing.exempt.form">
          <DialogContainer open={open} className={classes.modal}>
            <DialogHeader title="">
              <Box display="flex" alignItems="center">
                <img
                  src={warningIcon}
                  alt="warning"
                  className={classes.warningIcon}
                />
                <Typography variant="h5">
                  <FormattedMessage
                    id="invoices.exempt.title"
                    defaultMessage="Exempt Payment"
                  />
                </Typography>
              </Box>
            </DialogHeader>
            <DialogBody>
              <Typography variant="h6" className={classes.warningMessage}>
                <FormattedMessage
                  id="invoices.exempt.message"
                  defaultMessage="Select a reason for the exemption"
                />
              </Typography>
              <Box mt={2} ml={2}>
                <RHFRadioField
                  name={EXEMPTION_TYPE}
                  options={exemptions}
                  required
                  disabled={busy}
                />
              </Box>
            </DialogBody>
            <DialogFooter>
              <Button
                color="secondary"
                onClick={onCancel}
                data-testid="invoices.exempt.cancel.button"
                disabled={busy}
              >
                <FormattedMessage
                  id="invoices.exempt.cancel"
                  defaultMessage="Cancel"
                />
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                type="submit"
                data-testid="invoices.exempt.accept.button"
                disabled={!isDirty || !isValid}
                loading={busy}
              >
                <FormattedMessage
                  id="invoices.exempt.accept"
                  defaultMessage="Done"
                />
              </Button>
            </DialogFooter>
          </DialogContainer>
        </form>
      </FormProvider>
    </ValidationContext.Provider>
  );
};
